<section
  class="sav-category-detail-page"
  [attr.aria-label]="(categoryName() || 'Category') + ' category detail'"
>
  <div class="sav-category-detail-wrap">
    @if (isCategoryLoading()) {
      <section
        class="sav-category-hero sav-category-hero-skeleton"
        aria-label="Loading category overview"
        aria-busy="true"
      >
        <!-- The trail is known before the category loads, so it renders in the skeleton too —
             otherwise it appears when the data lands and pushes the whole page down. -->
        <app-breadcrumb [items]="crumbs()" />

        <div class="sav-category-hero-content">
          <span class="sav-category-skeleton sav-category-skeleton-title"></span>
          <span class="sav-category-skeleton sav-category-skeleton-text"></span>
          <span
            class="sav-category-skeleton sav-category-skeleton-text sav-category-skeleton-text-short"
          ></span>
        </div>
        <div class="sav-category-hero-stats">
          @for (_ of [0, 1, 2, 3]; track $index) {
            <article>
              <span class="sav-category-skeleton sav-category-skeleton-stat-value"></span>
              <span class="sav-category-skeleton sav-category-skeleton-stat-label"></span>
            </article>
          }
        </div>
      </section>

      <section
        class="sav-category-stores sav-category-stores-skeleton"
        aria-label="Loading top stores"
        aria-busy="true"
      >
        <header class="sav-section-headline">
          <span class="sav-category-skeleton sav-category-skeleton-heading"></span>
          <span class="sav-category-skeleton sav-category-skeleton-subtitle"></span>
        </header>
        <div class="sav-category-stores-grid">
          @for (_ of [0, 1, 2, 3]; track $index) {
            <article class="sav-category-store-card">
              <span class="sav-category-store-card-logo">
                <span class="sav-category-skeleton sav-category-skeleton-logo"></span>
              </span>
              <div class="sav-category-skeleton-card-copy">
                <span class="sav-category-skeleton sav-category-skeleton-store-name"></span>
                <span class="sav-category-skeleton sav-category-skeleton-store-meta"></span>
              </div>
            </article>
          }
        </div>
      </section>

      <section
        class="sav-trending-coupons-section sav-category-coupons-skeleton"
        aria-label="Loading offers"
        aria-busy="true"
      >
        <div class="sav-trending-coupons-wrap">
          <span
            class="sav-category-skeleton sav-category-skeleton-heading sav-category-skeleton-heading-centered"
          ></span>
          <div class="sav-trending-coupons-tabs">
            @for (_ of [0, 1, 2, 3]; track $index) {
              <span class="sav-category-skeleton sav-category-skeleton-tab"></span>
            }
          </div>
          <div class="sav-trending-coupons-grid">
            @for (_ of [0, 1, 2]; track $index) {
              <article class="sav-trending-coupon-card">
                <span class="sav-category-skeleton sav-category-skeleton-coupon-logo"></span>
                <div class="sav-trending-coupon-content">
                  <span class="sav-category-skeleton sav-category-skeleton-coupon-line"></span>
                  <span
                    class="sav-category-skeleton sav-category-skeleton-coupon-line sav-category-skeleton-coupon-line-short"
                  ></span>
                  <span class="sav-category-skeleton sav-category-skeleton-coupon-action"></span>
                </div>
              </article>
            }
          </div>
        </div>
      </section>
    } @else {
      <section class="sav-category-hero" aria-label="Category overview">
        <!-- Inside the tinted band, above the h1 — the same place every other page with a hero
             puts it (see blog-category-detail-page and coupon-collection-page). It used to sit
             above the band on the white strip, where it read as part of the header rather than as
             the top of this page. -->
        <app-breadcrumb [items]="crumbs()" />

        <h1 class="sav-category-hero-title">{{ heroHeadline() }}</h1>
        <div class="sav-category-hero-content">
          <p
            class="sav-category-hero-summary"
            [class.is-clamped]="shouldClampSummary() && !isSummaryExpanded()"
          >
            {{ heroSummary() }}
          </p>
          @if (shouldClampSummary()) {
            <button type="button" class="sav-hero-read-more" (click)="toggleSummary()">
              {{ isSummaryExpanded() ? 'Read Less' : 'Read More' }}
            </button>
          }
        </div>
        <!-- Live counts for THIS category, from the API's `statCards` (coupons, stores, average
             saving, products). Cards with a zero count are dropped, so the row disappears entirely
             rather than claiming numbers a category does not have. -->
        @if (statCards().length) {
          <div class="sav-category-hero-stats" aria-label="Category performance">
            @for (stat of statCards(); track stat.key) {
              <article>
                <strong>{{ stat.displayValue }}</strong>
                <span>{{ stat.label }}</span>
              </article>
            }
          </div>
        }
      </section>

      <!-- Categories with no stores would otherwise render a heading over an empty grid. -->
      @if (topStores().length) {
        <section id="stores" class="sav-category-stores" aria-label="Top category stores">
          <header class="sav-section-headline">
            <h2>{{ categoryName() ? 'Top stores in ' + categoryName() : 'Top stores' }}</h2>
            <p>
              Verified partners with high conversion, fresh inventory, and stable coupon
              reliability.
            </p>
          </header>
          <div class="sav-trending-stores-grid">
            @for (store of topStores(); track store.slug || store.name || $index) {
              <a
                class="sav-trending-store-card"
                [routerLink]="store.slug ? ['/', store.slug] : null"
              >
                <span class="sav-store-tile-logo-box">
                  <!-- The caption below names the store, so the artwork is decorative. -->
                  <app-store-logo
                    [logoUrl]="store.logoUrl"
                    [name]="store.name || 'Store'"
                    decorative
                  />
                </span>
                <span class="sav-store-tile-divider" aria-hidden="true"></span>
                <span class="sav-store-tile-store-meta">
                  @if (store.name) {
                    <span class="sav-store-tile-brand-name">{{ store.name }}</span>
                  }
                  <span class="sav-store-tile-count">{{ storeCountLabel(store) }}</span>
                </span>
              </a>
            }
          </div>
        </section>
      }

      <section class="sav-trending-coupons-section" aria-label="Trending coupons by subcategory">
        <div class="sav-trending-coupons-wrap">
          <h2>{{ currentTrendingCouponsHeading() }}</h2>
          <p>Verified codes and offers in this category, updated daily.</p>
          <div
            class="sav-trending-coupons-tabs"
            [attr.aria-label]="'Offer filters for ' + currentTrendingCouponsHeading()"
          >
            @for (tab of trendingCouponTabs; track tab.id) {
              <button
                type="button"
                [class.active]="selectedTrendingCouponTabId() === tab.id"
                (click)="selectTrendingCouponTab(tab.id)"
              >
                {{ tab.label }}
              </button>
            }
          </div>
          <div class="sav-trending-coupons-grid">
            @for (coupon of currentTrendingCoupons(); track coupon.id) {
              <article class="sav-trending-coupon-card">
                <a
                  class="sav-trending-coupon-brand"
                  [routerLink]="coupon.storeSlug ? ['/', coupon.storeSlug] : null"
                  [attr.aria-label]="coupon.storeName"
                >
                  <!-- `.sav-trending-coupon-brand-logo` stays on the slot: that global rule sizes it
                       and draws the ring. Decorative because the link's aria-label names the store. -->
                  <app-store-logo
                    class="sav-trending-coupon-brand-logo"
                    [logoUrl]="coupon.logoUrl"
                    [name]="coupon.storeName || 'Store'"
                    decorative
                  />
                </a>
                <div class="sav-trending-coupon-content">
                  <!-- Chip + hairline rule + verified, then the store name on its own line — the same
                     order as the home page's trending coupon card. The chip label is derived from
                     the existing actionType, no new data. -->
                  <div class="sav-trending-coupon-top-row">
                    <span class="sav-trending-coupon-type">{{
                      coupon.actionType === 'code' ? 'Code' : 'Deal'
                    }}</span>
                    <span class="sav-trending-coupon-verified">Verified</span>
                  </div>
                  @if (coupon.storeName) {
                    @if (coupon.storeSlug) {
                      <a class="sav-trending-coupon-store" [routerLink]="['/', coupon.storeSlug]">{{
                        coupon.storeName
                      }}</a>
                    } @else {
                      <span class="sav-trending-coupon-store">{{ coupon.storeName }}</span>
                    }
                  }
                  @if (coupon.title) {
                    <!-- Reads as body copy, not a heading: `.type-inherit` is the design system's
                       opt-out, so the name takes the card's type instead of the h3 scale. Matches
                       the home page's trending coupon card exactly. -->
                    <h3 class="type-inherit">{{ coupon.title }}</h3>
                  }
                  <div class="sav-trending-coupon-bottom-row">
                    @if (coupon.expires) {
                      <span>Expires : {{ coupon.expires }}</span>
                    }
                    @if (coupon.actionType === 'code') {
                      <!-- Label only — the code is revealed on the destination, not on the card. -->
                      <a [href]="coupon.actionUrl" target="_blank" rel="nofollow sponsored noopener">
                        <span class="sav-trending-coupon-code-label">{{ coupon.actionLabel }}</span>
                      </a>
                    } @else {
                      <a
                        [href]="coupon.actionUrl"
                        target="_blank"
                        rel="nofollow sponsored noopener"
                        class="deal"
                        >{{ coupon.actionLabel }}</a
                      >
                    }
                  </div>
                </div>
              </article>
            } @empty {
              <p class="sav-trending-coupons-empty">{{ currentTrendingCouponsEmptyText() }}</p>
            }
          </div>
        </div>
      </section>

      @if (hasSubCategories()) {
        <section class="sav-popular-categories-section" aria-label="Popular categories">
          <div class="sav-popular-categories-wrap">
            <!-- Live name only. The old `|| category.name` fallback read the static sample entry,
               which is fixed at whichever slug the component was first created with. -->
          <h2>Best {{ categoryName() }} Categories, Coupons &amp; Deals 2026</h2>
            <p>Narrow down to the sub-category you are shopping.</p>

            <!-- No tab row: there is one list here now — this category's own sub-categories — so
                 there is nothing to tab between. -->
            <div class="sav-popular-categories-grid">
              @for (category of visiblePopularCategories(); track category.label + $index) {
                <a
                  class="sav-popular-category-card"
                  [class]="'sav-popular-category-card ' + (category.thumb || '')"
                  [routerLink]="category.slug ? ['/categories', category.slug] : null"
                >
                  <span class="sav-popular-category-thumb" aria-hidden="true">
                    @if (category.imageUrl) {
                      <img [src]="category.imageUrl" [alt]="category.label" loading="lazy" />
                    } @else if (category.iconHtml) {
                      <span
                        class="sav-popular-category-icon"
                        [innerHTML]="category.iconHtml"
                      ></span>
                    }
                  </span>
                  <span class="sav-popular-category-label">{{ category.label }}</span>
                </a>
              }
            </div>

            <div class="sav-popular-categories-dots" aria-hidden="true">
              @for (index of popularCategorySlideIndexes(); track index) {
                <button
                  type="button"
                  [class.active]="currentPopularCategorySlide() === index"
                  [attr.aria-label]="'Go to category slide ' + (index + 1)"
                  (click)="selectPopularCategorySlide(index)"
                ></button>
              }
            </div>
          </div>
        </section>
      }

      @if (products().length) {
        <section class="sav-essentials" aria-label="Category products">
          <header class="sav-essentials-head">
            <div>
              <h2>{{ categoryName() ? categoryName() + ' Essentials' : 'Essentials' }}</h2>
              <p>Curated Collection 2026</p>
            </div>
            <button type="button">
              Shop All
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path
                  d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z"
                ></path>
              </svg>
            </button>
          </header>

          <div class="sav-product-grid">
            @for (product of products(); track product.id) {
              <app-product-card
                [title]="product.name"
                [image]="product.imageUrl"
                [hoverImage]="product.hoverImageUrl"
                [price]="product.newPrice"
                [oldPrice]="product.oldPrice"
                [external]="product.isExternal"
                [detailHref]="product.detailPath"
                [productId]="product.id"
              />
            }
          </div>
        </section>
      }

      <!-- The site-wide newsletter/event CTA, the same one the home page ends with. It replaced a
           static "Premium Savings Just For You" spotlight whose buttons were dead `href="#"` and
           whose "Join 4,200+ others saving right now" counter was invented. -->
      <app-big-sale-banner />

      @if (faqs().length) {
        <section class="sav-faq" aria-label="Frequently asked questions">
          <header class="sav-faq-head">
            <h2>FAQ's</h2>
            <p>
              Everything shoppers ask before using {{ categoryName() || 'this category' }} offers.
            </p>
          </header>

          <div class="sav-store-faq-list">
            @for (faq of faqs(); track faq.question || faq.answer || $index) {
              <details class="sav-store-faq-item" [open]="$index === 0">
                @if (faq.question) {
                  <summary>{{ faq.question }}</summary>
                }
                @if (faq.answer) {
                  <div [innerHTML]="faq.answer | trustedCmsHtml"></div>
                }
              </details>
            }
          </div>
        </section>
      }

      @if (allStores().length) {
        <section
          class="sav-trending-stores-section"
          aria-label="Trending stores you should check out"
        >
          <div class="sav-trending-stores-wrap">
            <h2>{{ categoryName() ? 'All stores in ' + categoryName() : 'All stores' }}</h2>
            <p>Every retailer we track in this category.</p>
            <div class="sav-trending-stores-grid">
              @for (store of allStores(); track store.slug || store.name || $index) {
                <a
                  class="sav-trending-store-card"
                  [routerLink]="store.slug ? ['/', store.slug] : null"
                >
                  <span class="sav-store-tile-logo-box">
                    <!-- The caption below names the store, so the artwork is decorative. -->
                    <app-store-logo
                      [logoUrl]="store.logoUrl"
                      [name]="store.name || 'Store'"
                      decorative
                    />
                  </span>
                  <span class="sav-store-tile-divider" aria-hidden="true"></span>
                  <span class="sav-store-tile-store-meta">
                    @if (store.name) {
                      <span class="sav-store-tile-brand-name">{{ store.name }}</span>
                    }
                    @if (hasCouponsCount(store.couponsCount)) {
                      <span class="sav-store-tile-count"
                        >{{ store.couponsCount }}+ Coupons &amp; Deals</span
                      >
                    }
                  </span>
                </a>
              }
            </div>
          </div>
        </section>
      }

      @if (longContentHtml()) {
        <section class="sav-longform" aria-label="Complete savings guide">
          <div class="sav-event-longform-wrap">
            <div class="sav-event-longform-card">
              <header class="sav-longform-head">
                <h2>{{ longformTitle() }}</h2>
                <p>A practical guide to saving in this category.</p>
              </header>

              <div
                class="sav-longform-content"
                [class.is-collapsed]="shouldCollapseLongContent() && !isLongContentExpanded()"
                [innerHTML]="longContentHtml() | trustedCmsHtml"
              ></div>
              @if (shouldCollapseLongContent()) {
                <button type="button" class="sav-longform-read-more" (click)="toggleLongContent()">
                  {{ isLongContentExpanded() ? 'Read Less' : 'Read More' }}
                </button>
              }
            </div>
          </div>
        </section>
      }

      @if (categoryBlogs().length > 0) {
        <!-- Tabs off: this list is already just the blogs assigned to this category, so a category
             filter above it has nothing to narrow. The tabs did not filter anything either — the
             component renders its full list regardless of the selected tab. -->
        <app-latest-insights [blogs]="categoryBlogs()" [showTabs]="false" />
      }
    }
  </div>
</section>
